import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import LogoutModal from '../components/common/LogoutModal';
import { useAuthStore } from '../store';

// Define the shape of our context
interface ModalContextType {
  // Modal visibility states
  isLogoutModalOpen: boolean;
  
  // Modal actions
  openLogoutModal: () => void;
  closeLogoutModal: () => void;
  
  // Modal handlers
  handleLogout: () => void;
}

// Create the context with a default value
const ModalContext = createContext<ModalContextType>({
  isLogoutModalOpen: false,
  openLogoutModal: () => {},
  closeLogoutModal: () => {},
  handleLogout: () => {},
});

// Custom hook to use the modal context
export const useModalContext = () => useContext(ModalContext);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  // Modal visibility states
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  
  // Get auth store logout function
  const { logout: authStoreLogout } = useAuthStore();
  
  // Modal actions - memoized to prevent unnecessary re-renders
  const openLogoutModal = useCallback(() => {
    setIsLogoutModalOpen(true);
  }, []);
  
  const closeLogoutModal = useCallback(() => {
    setIsLogoutModalOpen(false);
  }, []);
  
  // Modal handlers - these will be passed to components that need them
  const handleLogout = useCallback(() => {
    console.log("MODAL CONTEXT: Initiating logout...");
    
    // Close the modal first
    setIsLogoutModalOpen(false);
    
    // Use the auth store logout function to handle everything
    // The auth store uses authService.logout() which:
    // 1. Makes the logout API call with tokens still available
    // 2. Clears localStorage items after API call succeeds
    // 3. Redirects to login page
    console.log("MODAL CONTEXT: Calling auth store logout");
    authStoreLogout();
    
    // No need to manually clear localStorage or redirect
    // as the authService.logout() handles this properly
  }, [authStoreLogout]);
  
  return (
    <ModalContext.Provider
      value={{
        isLogoutModalOpen,
        openLogoutModal,
        closeLogoutModal,
        handleLogout,
      }}
    >
      {children}
      
      {/* Render all modals here, outside the main component tree */}
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onLogout={handleLogout}
        onCancel={closeLogoutModal}
      />
    </ModalContext.Provider>
  );
};

export default ModalProvider; 